// =====================================================================================
// FILE: src/services/viewLoader.js (MULTI-CLUB READY)
// =====================================================================================

/**
 * VenueFlow 2.0 - View Loader (Multi-Club Version)
 * 
 * This module is club-agnostic - it works for any club without code changes.
 * Just change the Airtable environment variables to point to a different base.
 * 
 * NAMING CONVENTIONS (must be consistent across all club bases):
 * - VIEW NAMES (Airtable sidebar): {ROUND}_{TEAM}_v_{OPPONENT}_{VENUE}
 *   Examples: OR_STK_v_COL_MCG, RD3_COL_v_BRI_MCG, RD5_CAR_v_RIC_MRV
 * 
 * - FIXTURE KEYS (Match ID field): {ROUND}_{TEAM}_v_{OPPONENT}_{YEAR}
 *   Examples: OR_STK_v_COL_26, RD3_COL_v_BRI_26, RD5_CAR_v_RIC_26
 * 
 * The conversion is automatic: replace venue suffix with year suffix.
 */

const TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;

// All table IDs for different modes
const TABLE_IDS = {
  out: process.env.REACT_APP_AIRTABLE_TABLE_ID,
  ingame: process.env.REACT_APP_AIRTABLE_INGAME_TABLE_ID,
  led: process.env.REACT_APP_AIRTABLE_LEDENTITLEMENTS_TABLE_ID,
  team: process.env.REACT_APP_AIRTABLE_TEAMLIST_TABLE_ID,
  iptv: process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID,
};

// ============================================================================
// CONFIGURATION
// ============================================================================

// Current season year suffix (update annually)
const CURRENT_YEAR_SUFFIX = '26';

// Venue codes that appear in view names (add more as needed)
const VENUE_CODES = [
  'MCG',
  'MRV',
  'GMHBA',
  'SCG',
  'GAB',
  'OPT',
  'AO',
  'ENGIE',
  'UTAS',
  'TIO',
  'METRICON',
  'PS',
  'GIANTS',    // Giants Stadium
  'MARVEL',    // Alternative Marvel name
  'DOCKLANDS', // Alternative Marvel name
];

// Try Metadata API first, fallback to empty list if it fails
const TRY_METADATA_API = true;

// ============================================================================
// MAIN EXPORTS
// ============================================================================

/**
 * Fetch available Airtable View names for the dropdown
 * Dynamically loads from Airtable Metadata API - no hardcoded club data
 * 
 * @param {string} mode - The current mode ('out', 'ingame', 'led', 'team', 'iptv')
 */
export async function fetchAvailableViews(mode = 'out') {
  const tableId = TABLE_IDS[mode] || TABLE_IDS.out;
  console.log(`📋 Loading available views for mode: ${mode} (table: ${tableId})...`);
  
  if (TRY_METADATA_API) {
    try {
      const dynamicViews = await fetchViewsFromMetadataAPI(tableId);
      if (dynamicViews && dynamicViews.length > 0) {
        console.log('✅ Loaded views from Metadata API:', dynamicViews);
        return dynamicViews;
      }
    } catch (error) {
      console.warn('⚠️ Metadata API failed:', error.message);
      console.warn('   Make sure your token has schema.bases:read scope');
    }
  }
  
  // No hardcoded fallback - return empty array
  // The app will show "Loading views..." or handle gracefully
  console.log('📋 No views found - check Airtable configuration');
  return [];
}

/**
 * Convert a view name to the corresponding fixture key for writes
 * 
 * Automatically converts venue suffix to year suffix:
 * - OR_STK_v_COL_MCG → OR_STK_v_COL_26
 * - RD3_COL_v_BRI_MRV → RD3_COL_v_BRI_26
 * - RD5_CAR_v_RIC_GMHBA → RD5_CAR_v_RIC_26
 * 
 * @param {string} viewName - The view name (e.g., "OR_STK_v_COL_MCG")
 * @returns {string} The fixture key (e.g., "OR_STK_v_COL_26")
 */
export function viewNameToFixtureKey(viewName) {
  if (!viewName) return '';
  
  let converted = viewName;
  
  // Replace any venue code suffix with the year suffix
  for (const venue of VENUE_CODES) {
    const pattern = new RegExp(`_${venue}$`, 'i');
    if (pattern.test(converted)) {
      converted = converted.replace(pattern, `_${CURRENT_YEAR_SUFFIX}`);
      break; // Only replace once (the suffix)
    }
  }
  
  // Log conversion for debugging (remove in production if noisy)
  if (converted !== viewName) {
    console.log(`📋 View → Fixture: "${viewName}" → "${converted}"`);
  }
  
  return converted;
}

/**
 * Get the fixture key for a given view (alias for viewNameToFixtureKey)
 */
export function getFixtureKeyForView(viewName) {
  return viewNameToFixtureKey(viewName);
}

// ============================================================================
// METADATA API
// ============================================================================

async function fetchViewsFromMetadataAPI(tableId) {
  if (!TOKEN || !BASE_ID) {
    throw new Error('Missing AIRTABLE_TOKEN or BASE_ID environment variables');
  }
  
  const url = `https://api.airtable.com/v0/meta/bases/${BASE_ID}/tables`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${TOKEN}`,
    },
  });
  
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Metadata API ${response.status}: ${text.substring(0, 200)}`);
  }
  
  const data = await response.json();
  
  // Find the target table by ID
  let table = data.tables?.find(t => t.id === tableId);
  
  // Fallback: try to find by name patterns if no tableId match
  if (!table?.views) {
    table = data.tables?.find(t => {
      const name = (t.name || '').toLowerCase();
      return (
        name.includes('out of game') ||
        name.includes('club content') ||
        name.includes('content builder')
      );
    });
  }
  
  if (!table?.views) {
    console.warn('⚠️ Could not find table in metadata. Available tables:', 
      data.tables?.map(t => `${t.name} (${t.id})`));
    throw new Error('Table not found in metadata');
  }
  
  console.log(`📋 Found table: "${table.name}" with ${table.views.length} views`);
  
  return extractFixtureViews(table.views);
}

/**
 * Extract fixture views from the views list
 * Filters out utility views and sorts by round number
 */
function extractFixtureViews(views) {
  const fixtureViews = views
    .map(v => v.name)
    .filter(isFixtureView);
  
  console.log(`📋 Found ${fixtureViews.length} fixture views out of ${views.length} total views`);
  
  return fixtureViews.sort((a, b) => extractRoundNumber(a) - extractRoundNumber(b));
}

/**
 * Check if a view name is a fixture view (not a utility view)
 * 
 * Fixture views follow the pattern: {ROUND}_{TEAM}_v_{OPPONENT}_{VENUE}
 * Examples: OR_STK_v_COL_MCG, RD3_COL_v_BRI_MRV, SOO_WA_v_VIC_OPT
 * 
 * Also includes special views like Venue_Testing
 */
function isFixtureView(viewName) {
  if (!viewName) return false;
  
  const upper = viewName.toUpperCase();
  
  // Include special views that don't follow the standard pattern
  const specialViews = [
    'VENUE_TESTING',
    'VENUE TESTING',
    'PRESEASON_TESTING',
    'PRESEASON TESTING',
  ];
  
  for (const special of specialViews) {
    if (upper === special || upper.includes(special)) return true;
  }
  
  // Exclude known utility view patterns
  const excludePatterns = [
    'ALL ROUNDS',
    'ALL RECORDS',
    'GRID VIEW',
    'GALLERY',
    'CALENDAR',
    'KANBAN',
    'TIMELINE',
    'FORM',
    'TEMPLATE',
    'ARCHIVE',
    'BACKUP',
    'OLD',
    'COPY',
    'DUPLICATE',
  ];
  
  for (const pattern of excludePatterns) {
    if (upper.includes(pattern)) return false;
  }
  
  // Must contain "_V_" or "_VS_" (versus pattern)
  if (!upper.includes('_V_') && !upper.includes('_VS_')) {
    return false;
  }
  
  // Should start with a round indicator
  const startsWithRound = (
    upper.startsWith('OR_') ||   // Opening Round
    upper.startsWith('OR ') ||
    /^RD\d+/.test(upper) ||      // Regular rounds (RD1, RD23, etc.)
    upper.startsWith('SOO') ||   // State of Origin
    upper.startsWith('QF') ||    // Qualifying Final
    upper.startsWith('EF') ||    // Elimination Final
    upper.startsWith('SF') ||    // Semi Final
    upper.startsWith('PF') ||    // Preliminary Final
    upper.startsWith('GF') ||    // Grand Final
    upper.startsWith('TEST') ||  // Testing views (TEST_*, Testing_*)
    upper.startsWith('TESTING')  // Testing views
  );
  
  return startsWithRound;
}

// ============================================================================
// SORTING
// ============================================================================

/**
 * Extract round number for sorting
 * 
 * Sort order:
 * -10: State of Origin (SOO)
 *  -1: Opening Round (OR)
 *   1-24: Regular season rounds (RD1-RD24)
 * 101: Qualifying Final
 * 102: Elimination Final
 * 103: Semi Final
 * 104: Preliminary Final
 * 105: Grand Final
 * 999: Unknown
 */
function extractRoundNumber(viewName) {
  if (!viewName) return 999;
  
  const str = String(viewName).toUpperCase();
  
  // State of Origin (before season)
  if (str.startsWith('SOO')) return -10;
  
  // Opening Round
  if (str.startsWith('OR_') || str.startsWith('OR ')) return -1;
  
  // Regular rounds: RD1 → 1, RD3 → 3, RD15 → 15, RD23 → 23
  const roundMatch = str.match(/^RD(\d+)/i);
  if (roundMatch) {
    return parseInt(roundMatch[1], 10);
  }
  
  // Finals (after regular season)
  if (str.startsWith('QF')) return 101;  // Qualifying Final
  if (str.startsWith('EF')) return 102;  // Elimination Final
  if (str.startsWith('SF')) return 103;  // Semi Final
  if (str.startsWith('PF')) return 104;  // Preliminary Final
  if (str.startsWith('GF')) return 105;  // Grand Final
  
  // Testing views (at the very end)
  if (str.startsWith('TEST')) return 998;
  
  return 999; // Unknown format goes to end
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

/**
 * Convert view names to dropdown options with formatted labels
 */
export function fixturesToViewOptions(views) {
  return views.map(viewName => ({
    value: viewName,
    label: formatViewLabel(viewName),
  }));
}

/**
 * Format view name into human-readable label
 * 
 * "RD3_STK_v_BRI_MRV" → "RD3 - STK v BRI (Marvel)"
 * "OR_COL_v_GWS_MCG" → "OR - COL v GWS (MCG)"
 */
function formatViewLabel(viewName) {
  if (!viewName) return viewName;
  
  // Venue display names
  const venueNames = {
    'MCG': 'MCG',
    'MRV': 'Marvel',
    'MARVEL': 'Marvel',
    'DOCKLANDS': 'Marvel',
    'GMHBA': 'GMHBA',
    'SCG': 'SCG',
    'GAB': 'Gabba',
    'OPT': 'Optus',
    'AO': 'Adelaide Oval',
    'ENGIE': 'Engie',
    'UTAS': 'UTAS',
    'TIO': 'TIO',
    'METRICON': 'Metricon',
    'PS': 'Perth Stadium',
    'GIANTS': 'Giants Stadium',
  };
  
  // Parse: RD3_STK_v_BRI_MRV or OR_COL_v_GWS_MCG
  const parts = viewName.split('_');
  
  // Expected format: [ROUND, TEAM1, 'v', TEAM2, VENUE]
  if (parts.length >= 5 && parts[2].toLowerCase() === 'v') {
    const round = parts[0];
    const team1 = parts[1];
    const team2 = parts[3];
    const venueCode = parts[4];
    const venueName = venueNames[venueCode.toUpperCase()] || venueCode;
    
    return `${round} - ${team1} v ${team2} (${venueName})`;
  }
  
  // Fallback: just replace underscores with spaces
  return viewName.replace(/_/g, ' ');
}
